import _ from "lodash";
/** external */
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classnames from "classnames";
/** internal */
import Question from "Worksheet/components/Question";
import {
  deleteQuestionOrganizationResource,
  deleteQuestionV2,
  deleteQuestion,
} from "Worksheet/modules/WorksheetModules";
import {
  deleteOrganizationResource,
  reorderSubQuestionInQuestionBank,
  reorderSubQuestionInQuestionBankV2,
  deleteQuestionLibrarySubQuestionV2,
} from "QuestionBank/modules/QuestionBankModule";
import {
  MODE_ASSESSMENT_VIEW,
  MODE_STUDENT_ATTEMPT,
  WORKSHEET_ELEMENT_IDS,
} from "Worksheet/modules/WorksheetConstants";
import { isMutationAndQueriesV2Enabled } from "Worksheet/modules/WorksheetUtils";
import { isWorksheetMutationsV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
/** relative */
import classes from "./SubQuestions.scss";

const _SubQuestions = props => {
  const {
    questions,
    parentResourceId,
    mode,
    deleteQuestionOrganizationResource,
    deleteQuestionV2,
    deleteQuestion,
    reorderSubQuestionInQuestionBank,
    reorderSubQuestionInQuestionBankV2,
    deleteQuestionLibrarySubQuestionV2,
    setQuestionRef,
    isFromLibrary,
    isFromInternalTool,
    deleteOrganizationResource,
    portalType,
    duplicateSectionChildResource,
    isGeneratingAIQuestions,
    getAddButton,
    addQuestionsInBetweenProps,
    isActiveQuestion,
    parentItemId,
    isQueriesV2Enabled,
    isLockScreenEnabled,
  } = props;

  const deleteSubQuestion = async (childResourceId, questionId) => {
    if (isFromInternalTool) {
      if (isMutationAndQueriesV2Enabled()) {
        await deleteQuestionLibrarySubQuestionV2({ questionId });
      } else {
        await deleteOrganizationResource({
          id: childResourceId,
          showToastMsg: false,
          isSubQuestion: true,
        });
      }
    } else {
      // Use V2 deletion when mutations V2 is enabled (sub-questions are in flat structure)
      if (isWorksheetMutationsV2Enabled()) {
        isQueriesV2Enabled
          ? deleteQuestionV2({
              questionId,
            })
          : deleteQuestion({
              questionId,
              parentId: parentResourceId,
              isSubQuestion: true,
            });
      } else {
        // Fall back to old implementation
        await deleteQuestionOrganizationResource({
          id: childResourceId,
          parentId: parentResourceId,
          isSubQuestion: true,
        });
      }
    }
  };

  const onMouseEnter = params => {
    addQuestionsInBetweenProps?.handleAddQuestionsInBetween?.(params);
  };

  const onMouseLeave = params => {
    addQuestionsInBetweenProps?.onMouseLeave?.(params);
  };

  const onBlur = params => {
    addQuestionsInBetweenProps?.onBlur?.(params);
  };

  const showAddQuestionInBetween =
    !isGeneratingAIQuestions &&
    isActiveQuestion &&
    !isFromInternalTool &&
    !isFromLibrary;

  const addButtonActiveIds = _.get(
    addQuestionsInBetweenProps,
    "addButtonActiveIds",
    []
  );
  const addButtonDropdownActiveId = _.get(
    addQuestionsInBetweenProps,
    "addButtonDropdownActiveId",
    null
  );
  //WORKSHEET_RESTRUCTURING: Remove multiPartResourceId
  const multiPartResourceId = parentResourceId + "multipart";
  const multipartItemId = parentItemId + "multipart";

  //WORKSHEET_RESTRUCTURING: Remove multiPartResourceId once isQueriesV2 is enabled
  const multipartId = isQueriesV2Enabled
    ? multipartItemId
    : multiPartResourceId;
  const isAddButtonActive = _.includes(addButtonActiveIds, multipartId);
  const firstQuestionResourceId = _.get(
    _.first(questions, {}),
    "resourceId",
    ""
  );
  const nextQuestionId = _.get(_.first(questions, {}), "id", "");

  const containerClasses = classnames({
    [classes.container]: true,
    [classes.containerV2]: showAddQuestionInBetween,
    [classes.containerV3]: showAddQuestionInBetween && !isGeneratingAIQuestions,
  });
  const addInBetweenClasses = classnames({
    [classes.addInBetween]: true,
    [classes.addInBetweenActive]: isAddButtonActive,
    [classes.addInBetweenDropdownActive]:
      addButtonDropdownActiveId === multipartId,
    [classes.addInBetweenV2]: true,
  });

  return (
    <div className={containerClasses}>
      {showAddQuestionInBetween && _.size(questions) && (
        <div
          className={addInBetweenClasses}
          onMouseEnter={() =>
            onMouseEnter({
              questionResourceId: null,
              parentResourceId,
              nextQuestionResourceId: firstQuestionResourceId,
              isAddingAtStartOfMultipart: true,
              questionId: null,
              parentItemId,
              nextQuestionId,
            })
          }
          onMouseLeave={() =>
            isQueriesV2Enabled
              ? onMouseLeave({ id: multipartItemId })
              : onMouseLeave({ resourceId: multiPartResourceId })
          }
          onBlur={e =>
            isQueriesV2Enabled
              ? onBlur({ e, id: multipartItemId })
              : onBlur({ e, resourceId: multiPartResourceId })
          }
          id={WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID}
        >
          {isAddButtonActive &&
            getAddButton({
              ..._.omit(addQuestionsInBetweenProps, [
                "handleAddQuestionsInBetween",
                "onMouseLeave",
                "onBlur",
                "addButtonActiveIds",
                "addButtonDropdownActiveId",
              ]),
              isSubQuestion: true,
              showAddInstructions: true,
            })}
        </div>
      )}
      {_.map(questions, (question, index) => {
        const questionId = _.get(question, "id");
        const questionResourceId = _.get(question, "resourceId", "");
        const nextQuestion = questions[index + 1];
        const nextQuestionResourceId = _.get(nextQuestion, "node.id", "");
        const isLastSubQuestion = index === _.size(questions) - 1;
        const isFirstSubQuestion = index === 0;
        const nextQuestionId = _.get(nextQuestion, "id", "");

        //WORKSHEET_RESTRUCTURING:Remove questionResourceId
        const id = isQueriesV2Enabled ? questionId : questionResourceId;

        const isAddButtonActive = _.includes(addButtonActiveIds, id);

        const addInBetweenClasses = classnames({
          [classes.addInBetween]: true,
          [classes.addInBetweenActive]: isAddButtonActive,
          [classes.addInBetweenDropdownActive]:
            addButtonDropdownActiveId === id,
        });
        const handleMoveUp = async () => {
          if (isFirstSubQuestion || !isFromInternalTool) return;
          const toIndex = index - 1;
          const putAfterItemId =
            toIndex === 0
              ? null
              : _.get(questions, [toIndex - 1, "resourceId"], null);
          const fromIndex = index;

          if (isMutationAndQueriesV2Enabled()) {
            await reorderSubQuestionInQuestionBankV2({
              parentQuestionId: parentItemId,
              questionId,
              putAfterQuestionId:
                toIndex === 0
                  ? null
                  : _.get(questions, [toIndex - 1, "id"], null),
              fromIndex,
              toIndex,
            });
          } else {
            await reorderSubQuestionInQuestionBank({
              parentResourceId,
              questionResourceId,
              fromIndex,
              toIndex,
              putAfterItemId,
            });
          }
        };

        const handleMoveDown = async () => {
          if (isLastSubQuestion || !isFromInternalTool) return;
          const toIndex = index + 1;
          const putAfterItemId = _.get(
            questions,
            [toIndex, "resourceId"],
            null
          );
          const fromIndex = index;

          if (isMutationAndQueriesV2Enabled()) {
            await reorderSubQuestionInQuestionBankV2({
              parentQuestionId: parentItemId,
              questionId,
              putAfterQuestionId: _.get(questions, [toIndex, "id"], null),
              fromIndex,
              toIndex,
            });
          } else {
            await reorderSubQuestionInQuestionBank({
              parentResourceId,
              questionResourceId,
              fromIndex,
              toIndex,
              putAfterItemId,
            });
          }
        };

        return (
          <div key={questionId}>
            <Question
              mode={mode}
              onDeleteClick={deleteSubQuestion}
              duplicateSectionChildResource={duplicateSectionChildResource}
              setQuestionRef={setQuestionRef}
              isFromLibrary={isFromLibrary}
              isFromInternalTool={isFromInternalTool}
              portalType={portalType}
              parentItemId={parentItemId}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              isFirstSubQuestion={isFirstSubQuestion}
              isLastSubQuestion={isLastSubQuestion}
              {...question}
              isLockScreenEnabled={isLockScreenEnabled}
            />
            {showAddQuestionInBetween && !isLastSubQuestion && (
              <div
                className={addInBetweenClasses}
                onMouseEnter={() =>
                  onMouseEnter({
                    questionResourceId,
                    parentResourceId,
                    nextQuestionResourceId,
                    questionId,
                    parentItemId,
                    nextQuestionId,
                  })
                }
                onMouseLeave={() =>
                  isQueriesV2Enabled
                    ? onMouseLeave({ id: questionId })
                    : onMouseLeave({ resourceId: questionResourceId })
                }
                onBlur={e =>
                  isQueriesV2Enabled
                    ? onBlur({ e, id: questionId })
                    : onBlur({ e, resourceId: questionResourceId })
                }
                id={WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID}
              >
                {isAddButtonActive &&
                  getAddButton({
                    ..._.omit(addQuestionsInBetweenProps, [
                      "handleAddQuestionsInBetween",
                      "onMouseLeave",
                      "onBlur",
                      "addButtonActiveIds",
                      "addButtonDropdownActiveId",
                    ]),
                    isSubQuestion: true,
                    showAddInstructions: true,
                  })}
              </div>
            )}
            {mode === MODE_ASSESSMENT_VIEW &&
            isFromLibrary &&
            index !== questions.length - 1 ? (
              <div className={classes.divider} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

_SubQuestions.propTypes = {
  questions: PropTypes.array,
  parentResourceId: PropTypes.string.isRequired,
  isFromLibrary: PropTypes.bool,
};

_SubQuestions.defaultProps = {
  questions: [],
};

const mapActionCreators = {
  deleteQuestionOrganizationResource,
  deleteOrganizationResource,
  deleteQuestionV2,
  deleteQuestion,
  reorderSubQuestionInQuestionBank,
  reorderSubQuestionInQuestionBankV2,
  deleteQuestionLibrarySubQuestionV2,
};

const mapStateToProps = state => {
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  return {
    isGeneratingAIQuestions,
  };
};

const SubQuestionsComponent = connect(
  mapStateToProps,
  mapActionCreators
)(_SubQuestions);

/**
 *
 * @param {*} props
 * @returns Sub Question Render for Student Attempt Mode
 * @description Only sending required props
 */
const StudentSubQuestionComponent = props => {
  const {
    questions,
    mode,
    setQuestionRef,
    isFromLibrary,
    isFromInternalTool,
    portalType,
    parentItemId,
    isLockScreenEnabled,
  } = props;

  const cachedNoop = useCallback(() => {}, []);

  return (
    <div className={classes.container}>
      {_.map(questions, (question, index) => {
        const questionId = _.get(question, "id");
        const isLastSubQuestion = index === _.size(questions) - 1;
        const isFirstSubQuestion = index === 0;

        return (
          <div key={questionId}>
            <Question
              mode={mode}
              onMoveUp={cachedNoop}
              onMoveDown={cachedNoop}
              onDeleteClick={cachedNoop}
              duplicateSectionChildResource={cachedNoop}
              setQuestionRef={setQuestionRef}
              isFromLibrary={isFromLibrary}
              isFromInternalTool={isFromInternalTool}
              portalType={portalType}
              parentItemId={parentItemId}
              isFirstSubQuestion={isFirstSubQuestion}
              isLastSubQuestion={isLastSubQuestion}
              isLockScreenEnabled={isLockScreenEnabled}
              {...question}
            />
          </div>
        );
      })}
    </div>
  );
};

const SubQuestions = props => {
  const { mode } = props;

  if (mode === MODE_STUDENT_ATTEMPT)
    return <StudentSubQuestionComponent {...props} />;

  return <SubQuestionsComponent {...props} />;
};

export default SubQuestions;
